/**
 * People / Team management (Phase 5b) — `/team`. Superadmin-only:
 * `RequireSession` (App.tsx) already proves a real session; this page
 * additionally hides itself from the nav for a non-superadmin
 * (`Navbar.tsx`) and its own two data calls (`GET /api/team/people`,
 * `GET /api/events/{id}/people`) are independently gated server-side
 * by `require_authz` (`people.team.list` = superadmin-only,
 * `people.event.list` = organizer-of-that-event/superadmin) — so a
 * non-superadmin who somehow lands here sees a clean 403 message, not
 * a broken page.
 *
 * Two independent sections, matching `app/api/people.py`'s two
 * surfaces:
 *   - TuviON admins (`/api/team/...`) — global superadmin roster.
 *   - Event organizers (`/api/events/{id}/...`) — scoped to whichever
 *     event `CurrentEventProvider`'s picker (the Navbar pill) has
 *     selected, same pattern `RosterTab.tsx` uses.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Shield, Trash2, UserPlus, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { extractErrorMessage } from '@/lib/utils';
import api from '@/services/api';
import { useAuthSession } from '@/state/authSession';
import { useCurrentEvent } from '@/state/currentEvent';
import { PeopleListResponse, PersonOut } from '@/types';

function formatExpiry(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ── shared invite form ──────────────────────────────────────────────

const InviteForm: React.FC<{
  placeholder: string;
  busy: boolean;
  onInvite: (email: string) => Promise<void>;
}> = ({ placeholder, busy, onInvite }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter an email address.');
      return;
    }
    setError(null);
    try {
      await onInvite(trimmed);
      setEmail('');
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not send the invite.'));
    }
  };

  return (
    <form className="flex flex-wrap items-start gap-2" onSubmit={(e) => void submit(e)}>
      <div className="min-w-[220px] flex-1">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          maxLength={320}
        />
        {error && <p className="mt-1 text-xs text-status-error">{error}</p>}
      </div>
      <Button type="submit" size="sm" disabled={busy} className="gap-1">
        <UserPlus className="size-4" />
        {busy ? 'Sending…' : 'Invite'}
      </Button>
    </form>
  );
};

// ── shared people table ─────────────────────────────────────────────

const PeopleTable: React.FC<{
  people: PersonOut[];
  pendingExpiryByEmail: Map<string, string>;
  onRevoke: (person: PersonOut) => Promise<void>;
  revokingUserId: number | null;
  emptyText: string;
  selfUserId: number | null;
}> = ({ people, pendingExpiryByEmail, onRevoke, revokingUserId, emptyText, selfUserId }) => {
  if (people.length === 0) {
    return <p className="px-1 py-4 text-sm text-fg-secondary">{emptyText}</p>;
  }
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className="grid grid-cols-[1.6fr_120px_1fr_100px] items-center gap-2 bg-bg-elevated px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">
        <span>Email</span>
        <span>Status</span>
        <span>Granted</span>
        <span className="text-right">Actions</span>
      </div>
      {people.map((p) => {
        const expiry = p.status === 'invited' ? pendingExpiryByEmail.get(p.email) : undefined;
        return (
          <div
            key={p.user_id}
            className="grid grid-cols-[1.6fr_120px_1fr_100px] items-center gap-2 border-t border-border px-3 py-2 text-sm"
          >
            <span className="flex items-center gap-1.5 truncate">
              <Mail className="size-3.5 shrink-0 text-fg-secondary" />
              {p.email}
              {p.user_id === selfUserId && (
                <span className="text-xs text-fg-secondary">(you)</span>
              )}
            </span>
            <span>
              {p.status === 'active' ? (
                <span className="rounded-full bg-status-online/15 px-2 py-0.5 text-xs text-status-online">
                  active
                </span>
              ) : (
                <span
                  className="rounded-full bg-status-warning/15 px-2 py-0.5 text-xs text-status-warning"
                  title={expiry ? `Invite expires ${formatExpiry(expiry)}` : undefined}
                >
                  invited
                </span>
              )}
            </span>
            <span className="text-xs text-fg-secondary">
              {p.granted_at ? formatExpiry(p.granted_at) : '—'}
            </span>
            <span className="text-right">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={revokingUserId === p.user_id}
                onClick={() => void onRevoke(p)}
                className="gap-1 text-status-error hover:bg-status-error/10 hover:text-status-error"
              >
                <Trash2 className="size-3.5" />
                Revoke
              </Button>
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ── TuviON admins section ───────────────────────────────────────────

const TeamAdminsSection: React.FC = () => {
  const { principal } = useAuthSession();
  const [data, setData] = useState<PeopleListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.listTeamPeople();
      setData(result);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not load the admin team.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (email: string) => {
    setInviteBusy(true);
    try {
      await api.inviteTeamPerson(email);
      await load();
    } finally {
      setInviteBusy(false);
    }
  };

  const revoke = async (person: PersonOut) => {
    setError(null);
    setRevokingUserId(person.user_id);
    try {
      await api.revokeTeamPerson(person.user_id);
      await load();
    } catch (err) {
      // Surface the last-admin guard (409) and any other failure
      // inline rather than silently no-op'ing — this is the one
      // action on this page that can lock the whole team out if it
      // silently "succeeded" when it didn't.
      setError(extractErrorMessage(err, 'Could not revoke this admin.'));
    } finally {
      setRevokingUserId(null);
    }
  };

  const pendingExpiryByEmail = new Map((data?.pending_invites ?? []).map((p) => [p.email, p.expires_at]));

  return (
    <Card className="p-4">
      <CardContent className="space-y-3 p-0">
        <div className="flex items-center gap-2">
          <Shield className="size-4 text-accent" />
          <h2 className="text-base font-semibold">TuviON admins</h2>
        </div>
        <p className="text-xs text-fg-secondary">
          Full access to every event and fleet surface. Invite carefully — the last remaining
          admin can't be revoked.
        </p>

        <InviteForm placeholder="new-admin@example.com" busy={inviteBusy} onInvite={invite} />

        {error && <p className="text-sm text-status-error">{error}</p>}

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-fg-secondary">
            <Spinner size={16} /> loading…
          </div>
        ) : (
          <PeopleTable
            people={data?.people ?? []}
            pendingExpiryByEmail={pendingExpiryByEmail}
            onRevoke={revoke}
            revokingUserId={revokingUserId}
            emptyText="No admins yet."
            selfUserId={principal?.user_id ?? null}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ── event organizers section ────────────────────────────────────────

const EventOrganizersSection: React.FC = () => {
  const { events, currentEvent, currentEventId, setCurrentEventId, loading: loadingEvents } =
    useCurrentEvent();
  const [data, setData] = useState<PeopleListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<number | null>(null);

  const load = useCallback(async (eventId: number) => {
    setLoading(true);
    try {
      const result = await api.listEventPeople(eventId);
      setData(result);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not load this event’s organizers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentEventId == null) {
      setData(null);
      return;
    }
    void load(currentEventId);
  }, [currentEventId, load]);

  const invite = async (email: string) => {
    if (currentEventId == null) return;
    setInviteBusy(true);
    try {
      await api.inviteEventPerson(currentEventId, email);
      await load(currentEventId);
    } finally {
      setInviteBusy(false);
    }
  };

  const revoke = async (person: PersonOut) => {
    if (currentEventId == null) return;
    setError(null);
    setRevokingUserId(person.user_id);
    try {
      await api.revokeEventPerson(currentEventId, person.user_id);
      await load(currentEventId);
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not revoke this organizer.'));
    } finally {
      setRevokingUserId(null);
    }
  };

  const pendingExpiryByEmail = new Map((data?.pending_invites ?? []).map((p) => [p.email, p.expires_at]));

  return (
    <Card className="p-4">
      <CardContent className="space-y-3 p-0">
        <div className="flex items-center gap-2">
          <Users className="size-4 text-accent" />
          <h2 className="text-base font-semibold">Event organizers</h2>
        </div>
        <p className="text-xs text-fg-secondary">
          Full control over one event's fleet, roster, and stages — scoped to that event only.
        </p>

        {loadingEvents ? (
          <div className="flex items-center gap-2 py-2 text-sm text-fg-secondary">
            <Spinner size={16} /> loading events…
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            Create an event on the Event tab before inviting organizers.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <label className="text-xs text-fg-secondary">Event</label>
              <select
                value={currentEventId ?? ''}
                onChange={(e) => setCurrentEventId(e.target.value ? Number(e.target.value) : null)}
                className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
              >
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                  </option>
                ))}
              </select>
            </div>

            {currentEvent && (
              <>
                <InviteForm
                  placeholder={`organizer@example.com — for ${currentEvent.name}`}
                  busy={inviteBusy}
                  onInvite={invite}
                />

                {error && <p className="text-sm text-status-error">{error}</p>}

                {loading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-fg-secondary">
                    <Spinner size={16} /> loading…
                  </div>
                ) : (
                  <PeopleTable
                    people={data?.people ?? []}
                    pendingExpiryByEmail={pendingExpiryByEmail}
                    onRevoke={revoke}
                    revokingUserId={revokingUserId}
                    emptyText="No organizers on this event yet."
                    selfUserId={null}
                  />
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── page ─────────────────────────────────────────────────────────────

export const TeamPage: React.FC = () => {
  return (
    <div className="mx-auto mb-8 mt-4 max-w-4xl px-2 sm:px-6">
      <div className="mb-5 flex items-center gap-3">
        <Shield className="h-7 w-7 text-accent" />
        <div>
          <h1 className="text-2xl font-semibold leading-tight">Team</h1>
          <p className="text-sm text-fg-secondary">
            Manage who has admin access and who organizes which events.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <TeamAdminsSection />
        <EventOrganizersSection />
      </div>
    </div>
  );
};

export default TeamPage;
